import React, { useEffect, useState } from "react";
import {
  Phone,
  Mail,
  Navigation,
  MessageCircle,
  Building2,
  Map as MapIcon,
  Loader2,
  AlertTriangle,
  ChevronRight
} from "lucide-react";
import api from "../api/Api"; 
import { toast } from "sonner"; 

const ContactOfficePage = () => {
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState(null);

  // --- FETCH DATA FROM API ---
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const res = await api.get("/contact/contact-details"); 
        if (res.data.success) {
          setContactData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load contact info", err);
        toast.error("Could not load contact details");
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, []);

  const openLink = (url) => {
    if (url) window.open(url, "_blank");
  };

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: "#f8fafc" }}>
        <Loader2 size={36} className="text-primary animate-spin mb-3" />
        <p className="text-muted fw-bold small text-uppercase" style={{ letterSpacing: "1px", animation: "pulse 1.5s infinite" }}>
          Loading Support...
        </p>
        <style>{`
          .animate-spin { animation: spin 1s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
        `}</style>
      </div>
    );
  }

  // --- ERROR STATE ---
  if (!contactData)
    return (
      <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-light p-4">
        <div className="bg-white p-4 rounded-4 shadow-sm text-center w-100" style={{ maxWidth: "400px" }}>
          <AlertTriangle size={40} className="text-warning mb-3 mx-auto" />
          <h5 className="fw-bold text-dark mb-2">Support Unavailable</h5>
          <p className="text-muted small mb-4">We couldn't connect to our support servers. Please check your connection.</p>
          <button className="btn btn-primary w-100 rounded-pill fw-bold native-press" onClick={() => window.location.reload()}>
            Retry Connection
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f4f4f5", paddingBottom: "20px" }}>
      
      {/* === 1. PREMIUM HEADER === */}
      <div
        className="position-relative overflow-hidden text-center"
        style={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          padding: "40px 20px 70px 20px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <div className="position-relative z-index-1 animate-fade-in">
          <h2 className="fw-bold text-white mb-1" style={{ letterSpacing: "-0.5px", fontSize: "1.75rem" }}>Help & Support</h2>
          <p className="text-white-50 small mb-0 fw-semibold" style={{ fontSize: "0.9rem" }}>
            We're here to help you 24/7.
          </p>
        </div>
      </div>

      {/* === 2. MAIN CONTENT AREA === */}
      <div className="px-3 position-relative z-index-2" style={{ maxWidth: "600px", margin: "0 auto", marginTop: "-35px" }}>
        
        {/* A. SLEEK ACTION HUB (iOS Settings Style) */}
        <div className="bg-white rounded-4 mb-4 animate-slide-up overflow-hidden" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)" }}>
          
          {/* WhatsApp Row */}
          <div 
            className="d-flex align-items-center justify-content-between p-3 cursor-pointer native-row"
            onClick={() => openLink(contactData.whatsapp?.link)}
          >
            <div className="d-flex align-items-center gap-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: "40px", height: "40px", backgroundColor: "#dcfce7", color: "#16a34a" }}>
                <MessageCircle size={20} strokeWidth={2.5} />
              </div>
              <div>
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.95rem" }}>{contactData.whatsapp?.label || "Chat on WhatsApp"}</h6>
                <span className="text-muted" style={{ fontSize: "0.75rem" }}>Fastest response time</span>
              </div>
            </div>
            <ChevronRight size={18} className="text-muted opacity-50" />
          </div>

          <hr className="m-0 opacity-10" style={{ backgroundColor: "#000" }} />

          {/* Call Row */}
          <div 
            className="d-flex align-items-center justify-content-between p-3 cursor-pointer native-row"
            onClick={() => openLink(`tel:${contactData.primary_support?.phone}`)}
          >
            <div className="d-flex align-items-center gap-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: "40px", height: "40px", backgroundColor: "#eff6ff", color: "#2563eb" }}>
                <Phone size={18} strokeWidth={2.5} />
              </div>
              <div>
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.95rem" }}>Call Support</h6>
                <span className="text-muted" style={{ fontSize: "0.75rem" }}>{contactData.primary_support?.phone}</span>
              </div>
            </div>
            <ChevronRight size={18} className="text-muted opacity-50" />
          </div>

          <hr className="m-0 opacity-10" style={{ backgroundColor: "#000" }} />

          {/* Email Row */}
          <div 
            className="d-flex align-items-center justify-content-between p-3 cursor-pointer native-row"
            onClick={() => openLink(`mailto:${contactData.primary_support?.email}`)}
          >
            <div className="d-flex align-items-center gap-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: "40px", height: "40px", backgroundColor: "#fef2f2", color: "#dc2626" }}>
                <Mail size={18} strokeWidth={2.5} />
              </div>
              <div>
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.95rem" }}>Email Operations</h6>
                <span className="text-muted" style={{ fontSize: "0.75rem" }}>operations@patratravels.com</span>
              </div>
            </div>
            <ChevronRight size={18} className="text-muted opacity-50" />
          </div>
        </div>

        {/* B. OFFICE HUBS LIST */}
        <div className="d-flex align-items-center justify-content-between mb-3 px-1 animate-slide-up" style={{ animationDelay: "0.1s" }}>
          <h6 className="fw-bold text-secondary text-uppercase mb-0" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>Our Offices</h6>
          <span className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>
            {contactData.offices?.length || 0} Locations
          </span>
        </div>

        <div className="d-flex flex-column gap-3">
          {contactData.offices?.map((loc, index) => (
            <div
              key={loc.id}
              className="card border-0 rounded-4 overflow-hidden bg-white animate-slide-up"
              style={{ 
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)", 
                border: "1px solid rgba(0,0,0,0.04)",
                animationDelay: `${0.15 + (index * 0.05)}s` 
              }}
            >
              <div className="card-body p-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                  
                  {/* Icon Squircle */}
                  <div 
                    className={`rounded-4 d-flex align-items-center justify-content-center flex-shrink-0 bg-${loc.color} bg-opacity-10 text-${loc.color}`}
                    style={{ width: "42px", height: "42px" }}
                  >
                    {loc.color === "primary" ? (
                      <Building2 size={20} strokeWidth={2} />
                    ) : (
                      <MapIcon size={20} strokeWidth={2} />
                    )}
                  </div>

                  {/* Office Info */}
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.95rem" }}>{loc.name}</h6>
                      <span
                        className={`badge bg-${loc.color} bg-opacity-10 text-${loc.color} rounded-pill fw-semibold`}
                        style={{ fontSize: "0.6rem", padding: "4px 8px" }}
                      >
                        {loc.type}
                      </span>
                    </div>
                    <p className="text-muted mb-0" style={{ fontSize: "0.8rem", lineHeight: "1.4" }}>
                      {loc.address}
                    </p>
                  </div>
                </div>

                {/* Soft Native Action Button - Redesigned to look like a utility link */}
                <button
                  onClick={() => openLink(loc.map_url)}
                  className="btn w-100 rounded-3 d-flex align-items-center justify-content-center gap-2 py-2 fw-bold native-row"
                  style={{ 
                    backgroundColor: "rgba(37, 99, 235, 0.05)", 
                    color: "#2563eb", 
                    border: "none",
                    fontSize: "0.85rem"
                  }}
                >
                  <Navigation size={16} strokeWidth={2.5} /> Get Directions
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* === GLOBAL COMPONENT STYLES === */}
      <style>{`
        /* Smooth native tactile press effect for list items */
        .native-row {
          transition: background-color 0.2s ease, transform 0.1s ease;
        }
        .native-row:active {
          background-color: #f8fafc !important;
          transform: scale(0.98);
        }
        
        .native-press {
          transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .native-press:active {
          transform: scale(0.96);
        }
        
        /* Smooth page load animations */
        .animate-slide-up {
          animation: slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out both;
        }
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ContactOfficePage;